import React, { useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import { HelpLink } from '@/features/help';
import { TourCardChrome } from './TourCardChrome';
import { TourMedia } from './TourMedia';
import type { ResolvedFrame, TourMedia as TourMediaType, TourStep } from '../types';

interface Props {
  step: TourStep;
  frame: ResolvedFrame;
  /** Resolved media for the current (step, frame). */
  media: TourMediaType;
  /** Resolved media for the next position — drives lazy preload. */
  preloadNext?: TourMediaType;
  stepNumber: number;
  totalSteps: number;
  frameNumber: number;
  frameCount: number;
  targetEl: HTMLElement | null;
  secondaryEl?: HTMLElement | null;
  isFirst: boolean;
  isLast: boolean;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
  onRemindLater?: () => void;
  hideRemindLater?: boolean;
  /** Jump-to-frame callback. Wired by Task 10's named stepper rail. */
  onGoToFrame?: (targetIndex: number) => void;
}

/**
 * Split-panel tour card body — a 640px-wide two-pane callout.
 *   - Left pane (~55%): typed media slot, 16:9 aspect-locked. Task 10 will
 *     add the named stepper rail under the media for multi-frame steps.
 *   - Right pane (~45%): title + body + Learn more link. Action row
 *     (Back/Next, optional Remind me later) is owned by `TourCardChrome`
 *     and rendered below both panes.
 *
 * Used for content-rich, multi-frame steps where the compact card is
 * cramped (RESEARCH §3 Variant 2). The TourCard dispatcher routes to
 * this component when `step.layout === 'split'` AND the viewport is wide
 * enough (Task 12).
 */
export const TourCardSplit: React.FC<Props> = ({
  step,
  frame,
  media,
  preloadNext,
  stepNumber,
  totalSteps,
  frameNumber,
  frameCount,
  targetEl,
  secondaryEl,
  isFirst,
  isLast,
  onNext,
  onBack,
  onSkip,
  onRemindLater,
  hideRemindLater,
  onGoToFrame,
}) => {
  const ctaRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    ctaRef.current?.focus();
  }, [step.id, frame.title, frame.body]);

  return (
    <TourCardChrome
      titleId={`tour-step-title-${step.id}`}
      bodyId={`tour-step-body-${step.id}`}
      stepNumber={stepNumber}
      totalSteps={totalSteps}
      targetEl={targetEl}
      secondaryEl={secondaryEl}
      placement={frame.placement}
      isFirst={isFirst}
      isLast={isLast}
      onNext={onNext}
      onBack={onBack}
      onSkip={onSkip}
      onRemindLater={onRemindLater}
      hideRemindLater={hideRemindLater}
      widthClassName="w-[640px]"
      primaryCtaRef={ctaRef}
    >
      <div
        className="mt-3 flex flex-row gap-4"
        data-testid="tour-card-split-body"
      >
        {/* Left pane: media-only. Each clip renders at its intrinsic
            aspect — width fills the pane, height follows the aspect ratio
            (so a portrait 9:16 clip is taller than a landscape 16:9 one,
            and the card grows accordingly). Forcing every clip into a
            single locked frame (the prior `forceAspect="16:9"` +
            object-cover behaviour) cropped most of the visible UI out of
            the portrait recordings; a height-bounded version (h-72) kept
            them un-cropped but shrank them to a thumbnail. Letting the
            natural aspect drive the slot is what reads as "normal" size
            for screen-recorded UI demos. */}
        <div className="basis-[55%] flex flex-col">
          {media.type !== 'none' ? (
            <TourMedia media={media} preloadNext={preloadNext} />
          ) : (
            // Empty placeholder reserves the same width so the right pane's
            // copy doesn't reflow when a step has no media.
            <div className="aspect-[16/9] w-full rounded-md bg-gray-50 ring-1 ring-gray-200" />
          )}
        </div>

        {/* Right pane: title / body / learn more / stepper rail. The rail
            sits at the bottom of the pane so the user's eye lands on
            title → body → next-step preview in reading order, while the
            action row (Back / Next) below the chrome owns forward
            navigation. */}
        <div className="basis-[45%] flex flex-col">
          <h2
            id={`tour-step-title-${step.id}`}
            className="text-lg font-semibold text-gray-900"
            aria-live="polite"
          >
            {frame.title}
          </h2>
          <p
            id={`tour-step-body-${step.id}`}
            className="mt-2 text-sm leading-5 text-gray-600"
            aria-live="polite"
          >
            {frame.body}
          </p>

          {frame.docsLink && (
            <div className="mt-3">
              <HelpLink
                to={frame.docsLink}
                label="Learn more"
                className="text-primary-700 hover:text-primary-900"
              />
            </div>
          )}

          {/* Named stepper rail. Renders only when the step has multiple
              frames. Each row is a <button> calling onGoToFrame with the
              row's index. Active row carries aria-current="step". When the
              consumer didn't provide onGoToFrame (rare — unit tests), the
              rows still render but as inert <span>s. */}
          {frameCount > 1 && (
            <ol
              className="mt-4 space-y-0.5 text-sm"
              aria-label={`Frames in ${step.title}`}
            >
              {Array.from({ length: frameCount }).map((_, i) => {
                const isActive = i === frameNumber - 1;
                const label = step.frames?.[i]?.title ?? `Frame ${i + 1}`;
                const dotClass = clsx(
                  'inline-block h-2 w-2 rounded-full',
                  isActive ? 'bg-primary-600' : 'bg-gray-300',
                );
                const rowClass = clsx(
                  'flex items-center gap-2 rounded px-2 py-1 text-left w-full',
                  isActive
                    ? 'text-gray-900 font-medium'
                    : 'text-gray-600 hover:bg-gray-50',
                );
                if (onGoToFrame) {
                  return (
                    <li key={i}>
                      <button
                        type="button"
                        onClick={() => onGoToFrame(i)}
                        aria-current={isActive ? 'step' : undefined}
                        className={rowClass}
                      >
                        <span className={dotClass} aria-hidden="true" />
                        <span>{label}</span>
                      </button>
                    </li>
                  );
                }
                return (
                  <li key={i}>
                    <span
                      aria-current={isActive ? 'step' : undefined}
                      className={rowClass}
                    >
                      <span className={dotClass} aria-hidden="true" />
                      <span>{label}</span>
                    </span>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </TourCardChrome>
  );
};
